// src/shared/ui/RolePicker.jsx
//
// The role checkbox list inside UserFormModal (and, now, the Account/Project
// role-grant modals — accounts/components/AccountRoleGrantModal.jsx,
// TeamMemberFormModal.jsx). Promoted from admin/components/RolePicker.jsx
// once a second module needed it, same as accounts/components/StatTile.jsx's
// own promotion — admin/components/RolePicker.jsx is now a thin re-export.
//
// Replaces the flat `.role-grid` of bare `<label><input type=checkbox/>
// <span>{name}</span>` pairs the old pages used, which showed a role's name
// and nothing else — so "org_admin" and "sow_org_admin" were
// indistinguishable without going to the Roles page to look up what each
// one grants.
//
// Groups by level (a role attaches at exactly one — see RoleLevel) because
// PLATFORM is a categorically different grant from ORG: it acts across every
// organization. Putting them in one undifferentiated list makes that easy to
// tick by accident. Callers that only ever pass one level's roles (Account/
// Project pages) naturally get a single group — no special-casing needed.
//
// Controlled: `value` is the array of selected role IDs (what
// CreateUserRequest.role_ids / AccountRoleGrantModal / TeamMemberFormModal
// want), and every change hands back a new array.

import { useMemo } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";
import StatusBadge from "./StatusBadge";

// Widest scope first. Every RoleLevel value in order — see
// domain/identity/value_objects/role_level.py — not just the two
// (platform/org) that usersApi.listAssignableRoles ever returns, since
// Account/Project callers pass account/project-level roles.
const LEVEL_ORDER = ["platform", "org", "account", "project"];

export default function RolePicker({ roles, value, onChange, emptyMessage }) {
  const selected = useMemo(() => new Set(value), [value]);

  const groups = useMemo(() => {
    const byLevel = new Map();
    roles.forEach((r) => {
      if (!byLevel.has(r.level)) byLevel.set(r.level, []);
      byLevel.get(r.level).push(r);
    });
    return [...byLevel.entries()]
      .sort((a, b) => LEVEL_ORDER.indexOf(a[0]) - LEVEL_ORDER.indexOf(b[0]))
      .map(([level, items]) => ({ level, items }));
  }, [roles]);

  function toggle(roleId) {
    onChange(selected.has(roleId) ? value.filter((id) => id !== roleId) : [...value, roleId]);
  }

  if (roles.length === 0) {
    return (
      <p className="text-sm text-muted border border-line rounded-card px-3 py-6 text-center">
        {emptyMessage || "No assignable roles. Create an organization-level role on the Roles page first."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="font-semibold text-ink">{value.length} selected</span>
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="inline-flex items-center gap-1 text-muted hover:text-ink"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      <div className="border border-line rounded-card max-h-[38vh] overflow-y-auto divide-y divide-line">
        {groups.map(({ level, items }) => (
          <div key={level}>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 sticky top-0 z-[1]">
              <StatusBadge map="roleLevel" status={level} size="sm" />
              {level === "platform" && (
                <span className="text-[11px] text-muted">
                  Acts across every organization
                </span>
              )}
            </div>

            {items.map((role) => {
              const checked = selected.has(role.id);
              return (
                <label
                  key={role.id}
                  className={[
                    "flex items-start gap-3 px-3 py-2 cursor-pointer transition-colors",
                    checked ? "bg-brand-50/60" : "hover:bg-gray-50",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(role.id)}
                    className="mt-0.5 shrink-0 accent-brand-600 w-4 h-4"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink">{role.name}</span>
                    {role.description && (
                      <span className="block text-xs text-muted mt-0.5">{role.description}</span>
                    )}
                    <span className="block text-[11px] text-muted mt-0.5">
                      {role.permissions.length === 0
                        ? "Grants nothing yet"
                        : `Grants ${role.permissions.length} permission${role.permissions.length === 1 ? "" : "s"}`}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

RolePicker.propTypes = {
  /** Assignable roles, as admin/api/rolesApi.js's listRoles() returns them. */
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      level: PropTypes.string.isRequired,
      description: PropTypes.string,
      permissions: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  /** Selected role IDs. */
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  /** Shown instead of the default org-oriented copy when `roles` is empty. */
  emptyMessage: PropTypes.string,
};
